import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  requestPasswordReset: (email) => api.post('/auth/password-reset-request', { email }),
  confirmPasswordReset: (token, newPassword) => api.post('/auth/password-reset-confirm', { token, new_password: newPassword }),
  checkUser: (email) => api.post('/auth/check-user', { email }),
  setPassword: (email, newPassword) => api.post('/auth/set-password', { email, new_password: newPassword }),
};

// Job Roles
export const jobRolesAPI = {
  getAll: () => api.get('/job-roles'),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Activities (Preboarding/Onboarding)
export const activitiesAPI = {
  getAll: (activityType) => api.get('/activities', { params: { activity_type: activityType } }),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  delete: (id) => api.delete(`/activities/${id}`),
  markComplete: (id) => api.post(`/activities/${id}/complete`),
  getMyCompletions: () => api.get('/my-activity-completions'),
};

// Training Nodes (Brain Map)
export const trainingNodesAPI = {
  getAll: (category) => api.get('/training-nodes', { params: { category } }),
  getById: (id) => api.get(`/training-nodes/${id}`),
  create: (data) => api.post('/training-nodes', data),
  update: (id, data) => api.put(`/training-nodes/${id}`, data),
  delete: (id) => api.delete(`/training-nodes/${id}`),
  bulkUpdatePositions: (updates) => api.put('/training-nodes/bulk-update-positions', updates),
  markComplete: (id) => api.post(`/training-nodes/${id}/complete`),
  getMyCompletions: () => api.get('/my-training-completions'),
  getMyAssigned: () => api.get('/my-assigned-trainings'),
  seedBrainMap: () => api.post('/seed-brain-map'),
};

// Quizzes
export const quizzesAPI = {
  getAll: () => api.get('/quizzes'),
  getById: (id) => api.get(`/quizzes/${id}`),
  create: (data) => api.post('/quizzes', data),
  update: (id, data) => api.put(`/quizzes/${id}`, data),
  delete: (id) => api.delete(`/quizzes/${id}`),
  submitAttempt: (quizId, answers) => api.post(`/quizzes/${quizId}/attempts`, { answers }),
  getAttempts: (quizId) => api.get(`/quizzes/${quizId}/attempts`),
  getMyAttempts: () => api.get('/my-quiz-attempts'),
};

// SOPs
export const sopsAPI = {
  getAll: (category) => api.get('/sops', { params: { category } }),
  getById: (id) => api.get(`/sops/${id}`),
  create: (data) => api.post('/sops', data),
  update: (id, data) => api.put(`/sops/${id}`, data),
  delete: (id) => api.delete(`/sops/${id}`),
  markComplete: (id) => api.post(`/sops/${id}/complete`),
  getCompletions: (id) => api.get(`/sops/${id}/completions`),
  getMyCompletions: () => api.get('/my-sop-completions'),
  // Quiz endpoints
  getQuiz: (sopId) => api.get(`/sops/${sopId}/quiz`),
  submitQuiz: (sopId, answers) => api.post(`/sops/${sopId}/quiz/submit`, { answers }),
  getQuizAttempts: (sopId) => api.get(`/sops/${sopId}/quiz/attempts`),
  getQuizAttemptsDashboard: () => api.get('/sop-quiz-attempts-dashboard'),
};

// External Certifications (GCP, HIPAA, IATA)
export const certificationsAPI = {
  getAll: (userId) => api.get('/certifications', { params: { user_id: userId } }),
  getMyCertifications: () => api.get('/my-certifications'),
  create: (data) => api.post('/certifications', data),
  update: (id, data) => api.put(`/certifications/${id}`, data),
  delete: (id) => api.delete(`/certifications/${id}`),
  getExpiring: (days = 90) => api.get('/certifications-expiring', { params: { days } }),
};

// Global Search
export const searchAPI = {
  search: (query) => api.get('/search', { params: { q: query } }),
};

// Lead Alerts
export const alertsAPI = {
  getAlerts: () => api.get('/lead-alerts'),
};

// Training Certificates
export const certificatesAPI = {
  getAll: (userId) => api.get('/training-certificates', { params: { user_id: userId } }),
  create: (data) => api.post('/training-certificates', data),
  getMine: () => api.get('/my-training-certificates'),
};

// PDF Certificate Generation
export const pdfCertificatesAPI = {
  getMyCertificates: () => api.get('/certificates/my-certificates'),
  getUserCertificates: (userId) => api.get(`/users/${userId}/certificates`),
  generateInitialTrainingPDF: (signature, comments = '') => {
    return api.post('/certificates/generate-initial', 
      { certificate_type: 'initial', signature, comments },
      { responseType: 'blob' }
    );
  },
  generateSOPTrainingPDF: (signature, comments = '') => {
    return api.post('/certificates/generate-sop', 
      { certificate_type: 'sop', signature, comments },
      { responseType: 'blob' }
    );
  },
};

// Curricula
export const curriculaAPI = {
  getAll: (jobRole) => api.get('/curricula', { params: { job_role: jobRole } }),
  getById: (id) => api.get(`/curricula/${id}`),
  create: (data) => api.post('/curricula', data),
  update: (id, data) => api.put(`/curricula/${id}`, data),
  delete: (id) => api.delete(`/curricula/${id}`),
};

// Assignments
export const assignmentsAPI = {
  getAll: (userId) => api.get('/assignments', { params: { user_id: userId } }),
  getById: (id) => api.get(`/assignments/${id}`),
  create: (data) => api.post('/assignments', data),
  bulkAssignByRole: (curriculumId, jobRole) => api.post('/assignments/bulk-by-role', { curriculum_id: curriculumId, job_role: jobRole }),
  completeTraining: (assignmentId, nodeId) => api.post(`/assignments/${assignmentId}/complete-training/${nodeId}`),
  completeSOP: (assignmentId, sopId) => api.post(`/assignments/${assignmentId}/complete-sop/${sopId}`),
  completeActivity: (assignmentId, activityId) => api.post(`/assignments/${assignmentId}/complete-activity/${activityId}`),
};

// Training Reports
export const reportsAPI = {
  getByUser: (userId) => api.get(`/training-reports/${userId}`),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Seed Data
export const seedAPI = {
  seedAll: () => api.post('/seed-data'),
};

export default api;
